<template>
  <q-select
    v-bind="{ ...$attrs, ...props }"
    ref="selectEl"
    class="select-xs ds-select"
    :class="{
      'ds-select--has-prepend-label': !!$slots.prepend,
    }"
    :popup-content-style="popupStyle"
    :popup-content-class="`ds-select-popup ${props.popupContentClass ?? ''}`"
  >
    <template
      v-if="props.placeholder && !props.useInput && !hasValueSelected"
      #selected
    >
      <span class="tw-opacity-60">{{ props.placeholder }}</span>
    </template>

    <template v-for="(_, slot) in slots" #[slot]="scope">
      <slot :name="slot" v-bind="scope || {}" />
    </template>
  </q-select>
</template>

<script lang="ts" setup>
import { QSelect, QSelectProps } from 'quasar';
import { computed, ref, useSlots, onMounted, nextTick } from 'vue';
import { get } from 'lodash-es';
import { Colors } from '@/api';

// extends works for enhancing intellisense, but not for type checking
// https://github.com/vuejs/core/issues/4294
interface Props {
  modelValue: unknown;
  options?: readonly unknown[] | undefined;
  outlined?: boolean;
  dense?: boolean;
  optionsDense?: boolean;
  dropdownIcon?: string;
  primaryColor?: Colors;
  placeholder?: string;
  useInput?: boolean;
  optionValue?: ((option: string | unknown) => unknown) | string | undefined;
  popupContentStyle?: Record<string, string>;
  popupContentClass?: string;
  menuSelf?: QSelectProps['menuSelf'];
  menuAnchor?: QSelectProps['menuAnchor'];
  shouldAutoFocus?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  outlined: true,
  dense: true,
  optionsDense: true,
  dropdownIcon: 'img:/icons/select_chevron.svg',
  primaryColor: Colors.BLUE,
  autoFocus: false,
});

const slots = useSlots() as Record<string, unknown>;

const selectEl = ref<QSelect | null>(null);

const hasValueSelected = computed(() => {
  if (props.modelValue) return true; // treat truthy values as selected even if they are invalid

  // handle valid falsy values
  const options = props.options;
  if (!Array.isArray(options)) return false;
  const valueKey = props.optionValue || 'value';
  return options.some(
    (option) =>
      option === props.modelValue ||
      get(option, valueKey as string) === props.modelValue
  );
});

// Auto-focus implementation - use onMounted to ensure ref is ready
onMounted(async () => {
  if (props.shouldAutoFocus) {
    await nextTick();
    // Wait one more frame to ensure Quasar component is fully initialized
    await new Promise((resolve) => requestAnimationFrame(resolve));
    focus();
  }
});

function focus() {
  if (selectEl.value) {
    selectEl.value.$el.querySelector('input')?.focus();
  }
}

function blur() {
  if (selectEl.value) {
    selectEl.value.$el.querySelector('input')?.blur();
  }
}

function filter() {
  if (selectEl.value) {
    selectEl.value.filter(props.modelValue as string);
  }
}

function hidePopup() {
  if (selectEl.value) {
    selectEl.value.hidePopup();
  }
}

const popupStyle = computed(() => ({
  '--q-primary': `rgb(var(--tw-color-${props.primaryColor}))`,
  ...(props.popupContentStyle || {}),
}));

defineExpose({
  focus,
  blur,
  filter,
  hidePopup,
});
</script>

<style scoped lang="scss">
.ds-select {
  :deep(.q-select__dropdown-icon) {
    font-size: 10px !important;
  }
}
.ds-select--has-prepend-label {
  :deep(.q-field__control) {
    padding-left: 0;
  }
}
</style>
